/**
 * @graphql agg
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import AkSpinner from '@atlaskit/spinner/spinner';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { VIEW_PAGE_BYLINE_FORGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';
import type {
	ForgeUIExtensionDataType,
	ForgeUIContentBylineItemExtensionType,
} from '@confluence/forge-ui/entry-points/types.ts';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { useWorkspaceARI } from '@confluence/forge-ui/entry-points/useWorkspaceARI.ts';
import {
	ForgeKeyboardShortcut,
	ForgeKeyboardShortcutVisualizer,
} from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut.ts';
import { ByLineForgeApp as ByLineForgeAppOriginal } from '@confluence/forge-ui/entry-points/apps.ts';
import { useInlineDialogCloseManager } from '@confluence/forge-ui/entry-points/useInlineDialogCloseManager.ts';
import { ResizeChangeListener } from '@confluence/forge-ui/entry-points/ResizeChangeListener.ts';
import { getContentType } from '@confluence/forge-ui/entry-points/contentType.ts';
import { getLocalId } from '@confluence/forge-ui/entry-points/localId.ts';
import { getAppId, getModuleKey } from '@confluence/forge-ui/entry-points/appData.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { usePageState } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getLogger } from '@confluence/logger';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { getSpaceType } from '@confluence/space-utils/entry-points/getSpaceType.ts';
import { useSpaceIdentifiers } from '@confluence/space-utils/entry-points/useSpaceIdentifiers.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	ByLineForgeAppinvokeExtensionMutation as ByLineForgeAppinvokeExtensionMutation$data,
	ByLineForgeAppinvokeExtensionMutationVariables as ByLineForgeAppinvokeExtensionMutation$variables,
} from './__types__/ByLineForgeAppinvokeExtensionMutation.ts';
import AddonIcon from './AK_AddonIcon.svg';
import { useMemoizedObj } from './byLineUtils.tsx';
import { validateContentPropertyData, validateEventData } from './validateContentPropertyData.tsx';

const EXPERIENCE_NAME = VIEW_PAGE_BYLINE_FORGE_EXPERIENCE;

// General content property query for reading all content properties
// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming
const GENERAL_CONTENT_PROPERTY_QUERY = gql`
	query GeneralContentPropertyQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				properties {
					nodes {
						id
						key
						version {
							number
						}
						value
					}
				}
			}
		}
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	padding: token('space.250'),
	maxWidth: '500px',
	maxHeight: '500px',
	overflowY: 'auto',
});

const i18n = defineMessages({
	appIcon: {
		id: 'content-header.forge.byline-app.app-icon-alt',
		defaultMessage: 'App Icon',
		description: 'Alt text of the App Icon',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ByLineForgeAppHeaderWrapper = styled.span<{
	isHidden: boolean;
	hideDot: boolean;
}>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => ({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		display: props.isHidden ? 'none' : 'inline-block',
		marginRight: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:before': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			content: props.isHidden || props.hideDot ? undefined : '"•"',
			display: 'inline-block',
			color: token('color.text.subtle'),
			padding: `0 ${token('space.100')} 0 0`,
			/* for IE <= 11 */
			textDecoration: 'none',
		},
		'&:hover': {
			textDecoration: 'none',
			textDecorationLine: 'none',
			cursor: 'pointer',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:before': {
				/* for IE <= 11 */
				textDecoration: 'none',
			},
		},
	}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.img({
	height: '14px',
	width: '14px',
	display: 'inline-block',
	verticalAlign: 'text-bottom',
	padding: `0 ${token('space.050')} 0 0`,
	color: token('color.icon'),
});

const forgeAppButtonStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	borderRadius: 'radius.small',
	display: 'inline-flex',
	font: 'font.body.small',
	color: 'color.text.subtle',
	gap: 'space.050',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	marginRight: 'space.0',
	padding: 'space.050',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		textDecoration: 'none',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

export type ByLineDPDataInvokeExtensionErrorsType = {
	message?: string;
	extensions?: {
		statusCode?: number;
	};
}[];

type ContextPayloadType = {
	cloudId: string;
	localId: string;
	extension: ForgeUIExtensionDataType;
};

const ByLineForgeAppTrigger = ({
	app,
	reference,
	defineTriggerRef,
	toggleClose,
	isOpen,
	extensionData,
	isHidden,
	hideDot,
	appTitle,
	setAppTitle,
}: {
	app: ForgeUIContentBylineItemExtensionType;
	reference?: React.Ref<HTMLElement> | null;
	defineTriggerRef?: (ref: React.Ref<HTMLElement> | null) => (node: HTMLElement | null) => void;
	toggleClose: () => void;
	isOpen: boolean;
	extensionData: ForgeUIExtensionDataType;
	isHidden: boolean;
	hideDot: boolean;
	appTitle: string;
	setAppTitle: (string) => void;
}) => {
	const { formatMessage } = useIntl();
	const appHasDynamicProperties: boolean = !!app.properties.dynamicProperties;
	const [contentPropertyKey, setContentPropertyKey] = useState<string | undefined>(
		app.properties.contentPropertyKey || '',
	);
	const { cloudId } = useSessionData();
	const workspaceARI = useWorkspaceARI();

	const [appIcon, setAppIcon] = useState<string | null>(app.properties.icon || null);
	const [appTooltip, setAppTooltip] = useState<string | null>(app.properties.tooltip || null);
	const [isLoading, setIsLoading] = useState<boolean>(appHasDynamicProperties);

	const [bylineDynamicPropertiesMutation] = useMutation<
		ByLineForgeAppinvokeExtensionMutation$data,
		ByLineForgeAppinvokeExtensionMutation$variables
	>(
		gql`
			mutation ByLineForgeAppinvokeExtensionMutation($input: InvokeExtensionInput!) {
				invokeExtension(input: $input) {
					success
					response {
						body
					}
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`,
		{
			client: getAGGClient(),
		},
	);

	const fetchDynamicProperties: () => Promise<void> = useCallback(async () => {
		const extensionId = app.id;
		const localId = getLocalId(extensionId);
		const contextPayload: ContextPayloadType = {
			cloudId,
			localId,
			extension: extensionData,
		};

		const result = await bylineDynamicPropertiesMutation({
			variables: {
				input: {
					entryPoint: 'dynamicProperties',
					contextIds: [workspaceARI],
					extensionId,
					payload: {
						context: contextPayload,
						...contextPayload, // TODO: This is temporary for Forge apps' backward compatibility
					},
				},
			},
		}).catch((e) => {
			return {
				data: {
					invokeExtension: {
						success: false,
						errors: [
							{
								message: e,
							},
						],
					},
				},
			};
		});

		setIsLoading(false);

		// @ts-expect-error The types here are a bit messy. Typegen for AGG should help solve that
		const { response, success } = result.data?.invokeExtension;
		// @ts-expect-error The types here are a bit messy. Typegen for AGG should help solve that
		const errors: ByLineDPDataInvokeExtensionErrorsType = result.data?.invokeExtension.errors;

		if (!success) {
			const logger = getLogger('by-line-apps');

			const errorMessages: string = errors.map((error) => error.message).join(' ');

			// prints error to aid forge developer && allow Ecosystem to track these errors
			logger.error`Failed to fetch dynamic properties for ByLine Forge app: ${app.id} with error: ${errorMessages}`;

			if (errorMessages !== 'User consent required') {
				getMonitoringClient().submitError(
					new Error(
						`Failed to fetch dynamic properties for ByLine Forge app: ${app.id} with error: ${errorMessages}`,
					),
					{ attribution: Attribution.ECOSYSTEM },
				);
			}

			return;
		}

		if (response.body) {
			const {
				body: { title: dynamicPropsTitle, tooltip: dynamicPropsTooltip, icon: dynamicPropsIcon },
			} = response;

			dynamicPropsTitle && setAppTitle(extensionTitle(app, dynamicPropsTitle));
			dynamicPropsTooltip && setAppTooltip(dynamicPropsTooltip);
			dynamicPropsIcon && setAppIcon(dynamicPropsIcon);
		}
	}, [app, cloudId, extensionData, bylineDynamicPropertiesMutation, workspaceARI, setAppTitle]);

	// Validation function for content property data
	const validateContentPropertyDataCallback = useCallback(
		(data: any): { title: string; icon: string; tooltip: string; contentPropertyKey: string } => {
			const defaultValues = {
				title: appTitle,
				icon: app.properties.icon || '',
				tooltip: app.properties.tooltip || '',
				contentPropertyKey: app.properties.contentPropertyKey || '',
			};

			return validateContentPropertyData(data, defaultValues);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// Validation function specifically for FORGE_CONTENT_PROPERTY_UPDATED event data
	const validateEventDataCallback = useCallback(
		(eventData: any): { title: string; icon: string; tooltip: string } => {
			const defaultValues = {
				title: appTitle,
				icon: app.properties.icon || '',
				tooltip: app.properties.tooltip || '',
			};
			return validateEventData(eventData, defaultValues);
		},
		[appTitle, app.properties.icon, app.properties.tooltip],
	);

	const runGeneralContentPropertyQuery = useCallback(() => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data: generalContentPropertyData, refetch: refetchQueryContentProperties } =
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<any>(GENERAL_CONTENT_PROPERTY_QUERY, {
				variables: {
					contentId: extensionData.content?.id,
				},
			});
		return { data: generalContentPropertyData, refetch: refetchQueryContentProperties };
	}, [extensionData]);

	// Run general content property query to get all properties (including IDs), if the app has a contentPropertyKey
	const queryResult = app.properties.contentPropertyKey ? runGeneralContentPropertyQuery() : null;

	const generalData = queryResult?.data || null;
	const refetchContentProperties = queryResult?.refetch;

	// Validate content property data and get clean values
	const validatedProperties = validateContentPropertyDataCallback(generalData);

	useEffect(() => {
		// refetch for updates on popup.content close
		// Only run if app has defined contentPropertyKey
		if (
			contentPropertyKey &&
			!!validatedProperties.contentPropertyKey &&
			validatedProperties.contentPropertyKey !== ''
		) {
			setContentPropertyKey(validatedProperties.contentPropertyKey);
			setIsLoading(false);
			// Update app properties with validated values
			if (validatedProperties.title && validatedProperties.title !== '') {
				setAppTitle(extensionTitle(app, validatedProperties.title));
			}
			if (validatedProperties.icon && validatedProperties.icon !== '') {
				setAppIcon(validatedProperties.icon);
			}
			if (validatedProperties.tooltip && validatedProperties.tooltip !== '') {
				setAppTooltip(validatedProperties.tooltip);
			}
		} else if (!isOpen && appHasDynamicProperties) {
			void fetchDynamicProperties();
		}
	}, [
		isOpen,
		appHasDynamicProperties,
		fetchDynamicProperties,
		validatedProperties.title,
		validatedProperties.icon,
		validatedProperties.tooltip,
		validatedProperties.contentPropertyKey,
		setAppTitle,
		setAppIcon,
		setAppTooltip,
		setContentPropertyKey,
		contentPropertyKey,
		app,
	]);

	// Event listener for FORGE_CONTENT_PROPERTY_UPDATED events
	useEffect(() => {
		const handleContentPropertyUpdate = async (event: CustomEvent) => {
			if (event.detail?.payload?.propertyKey !== app.properties.contentPropertyKey) {
				return;
			}

			// Refetch content properties to get the latest data and ensure proper validation
			// This is important when multiple apps use the same contentPropertyKey
			if (refetchContentProperties) {
				try {
					await refetchContentProperties();
				} catch (error) {
					// eslint-disable-next-line no-console
					console.warn('Failed to refetch content properties on event update:', error);
				}
			}

			// Validate the event data using the specific event validation method
			const validatedData = validateEventDataCallback(event.detail?.payload);

			// Update app properties with validated values
			if (validatedData.title && validatedData.title !== '') {
				setAppTitle(extensionTitle(app, validatedData.title));
			}
			if (validatedData.icon && validatedData.icon !== '') {
				setAppIcon(validatedData.icon);
			}
			if (validatedData.tooltip && validatedData.tooltip !== '') {
				setAppTooltip(validatedData.tooltip);
			}
		};

		// Only process if this event is for our content property key
		if (!contentPropertyKey || contentPropertyKey === '') {
			return () => {}; // Return empty cleanup function to maintain React lifecycle consistency
		}

		// Add event listener
		// @ts-ignore - typescript error since this is a custom event
		document.addEventListener(
			'forge.bridge.FORGE_CONTENT_PROPERTY_UPDATED',
			handleContentPropertyUpdate,
		);

		// Cleanup function to remove event listener
		return () => {
			// @ts-ignore - typescript error since this is a custom event
			document.removeEventListener(
				'forge.bridge.FORGE_CONTENT_PROPERTY_UPDATED',
				handleContentPropertyUpdate,
			);
		};
	}, [
		validateEventDataCallback,
		contentPropertyKey,
		setAppTitle,
		setAppIcon,
		setAppTooltip,
		app,
		refetchContentProperties,
	]);

	const ForgeAppButton = (
		<Pressable testId="byline-forge-app-button" xcss={forgeAppButtonStyles}>
			{appIcon && (
				<Image
					data-testid="byline-forge-app-image"
					src={appIcon}
					onError={(e) => {
						e.currentTarget.src = AddonIcon;
					}}
					alt={formatMessage(i18n.appIcon)}
				/>
			)}
			{appTitle}
		</Pressable>
	);

	const bylineForgeAppHeaderWrapper = (
		<ByLineForgeAppHeaderWrapper
			ref={defineTriggerRef && reference ? defineTriggerRef(reference) : null}
			onClick={toggleClose}
			isHidden={isHidden}
			hideDot={hideDot}
			data-testid="forge-byline-header-wrapper"
		>
			{isLoading ? (
				<AkSpinner size="small" testId="byline-forge-app-trigger-loading-spinner" />
			) : (
				ForgeAppButton
			)}
		</ByLineForgeAppHeaderWrapper>
	);

	return app.properties?.keyboardShortcut?.accelerator ? (
		<ForgeKeyboardShortcutVisualizer module={app} tooltip={appTooltip ?? ''}>
			{bylineForgeAppHeaderWrapper}
		</ForgeKeyboardShortcutVisualizer>
	) : (
		<Tooltip tag="span" content={appTooltip}>
			{bylineForgeAppHeaderWrapper}
		</Tooltip>
	);
};

type ByLineForgeAppProps = {
	app: ForgeUIContentBylineItemExtensionType;
	contentId: string;
	isHidden?: boolean;
	setShowAll?: (boolean) => void;
	hideDot?: boolean;
};

export const ByLineForgeApp: FC<ByLineForgeAppProps> = ({
	app,
	contentId,
	isHidden = false,
	setShowAll = () => {},
	hideDot = false,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activationId } = useSessionData();
	const isLivePage = useIsCurrentPageLive();
	const isOnEditRoute = useIsEditorPage();
	const isEditing = isLivePage || isOnEditRoute;

	const [isOpen, setIsOpen] = useState(false);
	const [popupRef, setPopupRef] = useState<HTMLElement | null>(null);
	const [triggerRef, setTriggerRef] = useState<HTMLElement | null>(null);
	const [appTitle, setAppTitle] = useState<string>(extensionTitle(app));

	const [{ routeName, spaceKey: spaceAlias }] = usePageState();
	const { spaceId, spaceKey } = useSpaceIdentifiers();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const extensionData: ForgeUIExtensionDataType = useMemoizedObj({
		type: FORGE_MODULE_BYLINE,
		content: {
			id: contentId,
			type: getContentType(routeName),
			subtype: isLivePage ? 'live' : null,
		},
		space: {
			id: spaceId,
			key: spaceKey || spaceAlias,
		},
	});

	const defineContentRef = (node: HTMLElement | null) => {
		setPopupRef(node);
	};

	const defineTriggerRef = (ref) => (node: HTMLElement | null) => {
		setTriggerRef(node);
		ref(node);
	};

	const onClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const toggleClose = useCallback(() => {
		// Run experience only while opening the popup
		!isOpen &&
			experienceTracker.start({
				name: EXPERIENCE_NAME,
				id: contentId,
				timeout: 10000,
			});

		setIsOpen(!isOpen);
	}, [setIsOpen, isOpen, experienceTracker, contentId]);

	const onInitialRender = useCallback(() => {
		experienceTracker.succeed({
			name: EXPERIENCE_NAME,
		});
		const appId = getAppId(app);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'invoked',
				actionSubject: 'forgeExtension',
				actionSubjectId: appId,
				containerType: 'space',
				containerId: spaceId,
				objectType: getContentType(routeName),
				objectId: contentId,
				source: FORGE_MODULE_BYLINE,
				attributes: {
					activationId,
					appId,
					moduleType: FORGE_MODULE_BYLINE,
					moduleKey: getModuleKey(app),
					spaceType: getSpaceType(spaceAlias),
					spaceId,
				},
			},
		}).fire();
	}, [
		activationId,
		app,
		contentId,
		routeName,
		createAnalyticsEvent,
		experienceTracker,
		spaceAlias,
		spaceId,
	]);

	const bridge = useMemo(
		() => ({
			onClose,
			close: onClose,
		}),
		[onClose],
	);

	useInlineDialogCloseManager({ onClose, popupRef, triggerRef });

	if (!spaceKey) {
		return null;
	}

	// Because the trigger is a separate component to the Forge modal we need to hide the div that the Forge app creates because the only thing we want it to render is the modal on trigger.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const InvisibleDiv = styled.div({
		display: 'none',
	});

	if (
		app.properties.viewportContainer === 'modal' &&
		fg('platform_add_modal_support_to_bylineitem_extension')
	) {
		return (
			<>
				<ByLineForgeAppTrigger
					app={app}
					appTitle={appTitle}
					setAppTitle={setAppTitle}
					isOpen={isOpen}
					toggleClose={toggleClose}
					extensionData={extensionData}
					isHidden={isHidden}
					hideDot={hideDot}
				/>
				{isOpen ? (
					<InvisibleDiv>
						<ByLineForgeAppOriginal
							app={app}
							extensionData={extensionData}
							onInitialRender={onInitialRender}
							onTearDown={onClose}
							bridge={bridge}
						/>
					</InvisibleDiv>
				) : null}
			</>
		);
	}

	return (
		<>
			<Popup
				shouldRenderToParent={!isEditing} // This gets messed up when in the editor
				testId="forge-action-popup"
				role="dialog"
				content={({ update: scheduleUpdate }) => (
					<ContentWrapper ref={defineContentRef} data-testid="forge-popup-content-wrapper">
						<ResizeChangeListener onUpdate={scheduleUpdate} popupRef={popupRef}>
							<ByLineForgeAppOriginal
								app={app}
								extensionData={extensionData}
								onInitialRender={onInitialRender}
								onTearDown={onClose}
								bridge={bridge}
							/>
						</ResizeChangeListener>
					</ContentWrapper>
				)}
				label={appTitle}
				isOpen={isOpen}
				placement="bottom-start"
				trigger={({ ref }) => {
					return (
						<ByLineForgeAppTrigger
							app={app}
							appTitle={appTitle}
							setAppTitle={setAppTitle}
							reference={ref}
							isOpen={isOpen}
							defineTriggerRef={defineTriggerRef}
							toggleClose={toggleClose}
							extensionData={extensionData}
							isHidden={isHidden}
							hideDot={hideDot}
						/>
					);
				}}
			/>
			{!isEditing && (
				// Don't want these in the editor since it could trigger when user is editing the document
				<ForgeKeyboardShortcut
					module={app}
					action={() => {
						setShowAll(true);
						setIsOpen(true);
					}}
				/>
			)}
		</>
	);
};
